import clsx from "clsx";
import { useDispatch } from "react-redux";

import { BRAND_NAME, FEATURE_FLAGS } from "@chef/constants";
import { Container, Skeleton, Button } from "@chef/components";
import { useMediaQuery } from "@chef/hooks";
import { useAppSelector, useCalendarData } from "@chef/state-management/hooks";
import {
  BillingQuery,
  selectIsEditModeOnForWeek,
  openModal,
  showNotification,
  useBillingQuery,
} from "@chef/state-management";
import { getCurrentWeekDataAndBasketFromCalendar } from "@chef/state-management/helpers";
import {
  formatDate,
  formatTimeInterval,
  isAccountPaused,
  padPostalCode,
  sendIntercomMessage,
} from "@chef/helpers";
import { capitalize } from "@chef/utils/string";
import { DeliveryTimeAdjustedWarning } from "@chef/smart-components";
import {
  ContentComponent,
  ISection,
  useSectionData,
} from "@chef/feature-sanity";
import { useFlag } from "@chef/feature-flags";

import { intl } from "./WeekStatus.Intl";

type WeekStatusProps = {
  week: number;
  year: number;
  sanityData: ISection | null;
};

export const WeekStatus = ({ week, year, sanityData }: WeekStatusProps) => {
  const dispatch = useDispatch();
  const { enabled: isWeekEditorRedesignEnabled } = useFlag(
    FEATURE_FLAGS.WEEK_EDITOR_REDESIGN,
  );
  const isEditModeOnForWeek = useAppSelector(
    selectIsEditModeOnForWeek({ week, year }),
  );

  const isRedesignEnabled = isWeekEditorRedesignEnabled && isEditModeOnForWeek;

  const [sanity] = useSectionData(sanityData);
  const [isMobile] = useMediaQuery(`(max-width: 768px )`);

  const { data: billingQuery, isLoading: isLoadingBilling } = useBillingQuery();

  const { calendar } = useCalendarData({ week, year });

  const currentWeekObject = getCurrentWeekDataAndBasketFromCalendar({
    calendar: calendar || [],
    week,
    year,
  });

  if (!currentWeekObject || !billingQuery) {
    return <Skeleton height={32.5} />;
  }

  const { isBeforeStartDateWeek, mealboxBasket, groceryBasket } =
    currentWeekObject || {};

  const {
    shippingDetails: {
      plannedDeliveryInformation: {
        deliveryDate = "",
        isFallback = false,
        from = "",
        to = "",
        cutOffDateTime = "",
      } = {},
      hasDelivery = false,
      addressInfo: { addressId = "" } = {},
    } = {},
  } = mealboxBasket || groceryBasket || {};

  const deliveryAddress:
    | BillingQuery["billing"]["addresses"][number]
    | undefined = billingQuery?.billing.addresses.find(
    (address) => address.addressId === addressId,
  );

  const fullDeliveryAddressString = `${
    deliveryAddress?.address
  }, ${padPostalCode(deliveryAddress?.postalCode || "")} ${
    deliveryAddress?.city
  }`;

  const isPausedAccount = isAccountPaused(billingQuery.billing.status);

  const isPausedAccountOrInactiveScheduler = isPausedAccount || !hasDelivery;

  const isActiveWeek =
    !isPausedAccountOrInactiveScheduler && !isBeforeStartDateWeek;

  let titleText: string;
  if (isRedesignEnabled) {
    titleText = intl.CONFIRM_DELIVERY;
  } else if (isActiveWeek) {
    titleText = intl.UPCOMING;
  } else if (isPausedAccount) {
    titleText = intl.SUBSCRIPTION_CANCELLED;
  } else {
    titleText = intl.PAUSED;
  }
  //temporary solution for reusing WeekStatus component with new design
  const showRedesign = isRedesignEnabled;
  const showActiveOldDesign = isActiveWeek && !showRedesign;
  const showInactiveOldDesign = !isActiveWeek && !showRedesign;

  const showOutlinedButton = !!(isActiveWeek || showRedesign);
  const showPrimaryButton = !isActiveWeek && !showRedesign;

  const isManageDelivery = isActiveWeek || showRedesign;

  const formatCutoffDate = formatDate(
    new Date(cutOffDateTime),
    `${brand.SHORT_DATE_FORMAT} HH:mm`,
  );

  const formatDeliveryDate = formatDate(
    new Date(deliveryDate),
    `EEEE ${brand.SHORT_DATE_FORMAT}`,
  );

  const handleOpenSchedulerModal = () => {
    if (isBeforeStartDateWeek) {
      showNotification({
        type: "warn",
        message: `${intl.TO_BOOK_ON_WEEK} ${week} ${intl.CUSTOMER_SERVICE_WILL_HELP}`,
      });

      sendIntercomMessage({
        prepopulatedMessage: intl.PREPOPULATED_MESSAGE(week),
        subject: intl.SUBJECT_OF_INTERCOME_MESSAGE,
      });

      return;
    } else {
      dispatch(openModal({ id: "WEEK_SCHEDULER_EDITOR", week, year }));
    }
  };

  const SanityBlock = () => {
    if (!sanity?.content) {
      return null;
    }
    return <ContentComponent block={sanity.content} />;
  };

  return (
    <>
      <Container
        outerClassName={clsx({
          "bg-informationBG": showActiveOldDesign,
          "bg-errorBG": showInactiveOldDesign,
        })}
      >
        <div className="flex items-center justify-between py-4">
          <div className="flex-grow min-w-0 ">
            <div
              className={clsx({
                "text-information": showActiveOldDesign,
                "text-error": showInactiveOldDesign,
              })}
            >
              <strong>{titleText}</strong>
            </div>
            <div
              className={clsx(
                isPausedAccountOrInactiveScheduler && "text-grey-1",
                "mt-2 text-sm",
              )}
            >
              <strong>
                {`${capitalize(formatDeliveryDate)}, ${
                  intl.TIME_PREFIX
                } ${formatTimeInterval(from, to, "m")} `}
              </strong>
              <p className="mr-4 truncate">{fullDeliveryAddressString}</p>
            </div>

            {isFallback && (
              <DeliveryTimeAdjustedWarning BlockContent={SanityBlock} />
            )}
            <div
              className={clsx(
                "my-2",
                isPausedAccountOrInactiveScheduler && "text-black",
                "text-sm",
              )}
            >
              {hasDelivery && !isBeforeStartDateWeek
                ? `${intl.MAKE_CHANGES_BY} ${formatCutoffDate}`
                : `${intl.BOOK_BY} ${formatCutoffDate}`}
            </div>
          </div>

          {!isPausedAccount && (
            <Button
              primary={showPrimaryButton}
              outlined={showOutlinedButton}
              className={clsx(showActiveOldDesign && "bg-white hover:bg-white")}
              loading={isLoadingBilling}
              onClick={handleOpenSchedulerModal}
              tiny={isMobile}
            >
              {isManageDelivery ? intl.MANAGE_DELIVERY : intl.ORDER_DELIVERY}
            </Button>
          )}
        </div>
      </Container>
    </>
  );
};

const brand = (
  {
    AMK: {
      SHORT_DATE_FORMAT: "d. MMM",
    },
    GL: {
      SHORT_DATE_FORMAT: "d. MMM",
    },
    LMK: {
      SHORT_DATE_FORMAT: "d MMM",
    },
    RN: {
      SHORT_DATE_FORMAT: "d. MMM",
    },
  } as const
)[BRAND_NAME];
